import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Assignee, AssigneeType, PropertyClient, PropertyValue } from 'api/clients/PropertyClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { convertPropertyValuesToAssignedPropertyValues } from 'common/PropertyValue/utils'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { upsertFilesProperties } from 'redux/actions/filesProperties'
import { RootState, ViewType } from 'types'
import { MaybeTransitionEffectPayload, runAfterTransitionEffects } from 'utils/workflows/runAfterTransitionEffects'
import { AddFileInline, ResponseData } from './AddFileInline'
import useIsMounted from 'ismounted'
import { observer } from 'mobx-react-lite'

interface Props {
  viewType: ViewType
  withPropertyValues?: PropertyValue[]
  emptyState?: boolean
  onCreateFile?: (type: AssigneeType, title: string) => Promise<MaybeTransitionEffectPayload>
  onUploadMediaFile?: () => void
  forceOpen?: boolean
}

export const AddRowInline = observer(
  ({ viewType, withPropertyValues, emptyState, onCreateFile, onUploadMediaFile, forceOpen }: Props) => {
    const dispatch = useDispatch()
    const properties = useSelector(({ properties }: RootState) => properties)
    const isMounted = useIsMounted()

    const assignPropertyValues = useCallback(
      async (assignee: Assignee, payload?: MaybeTransitionEffectPayload) => {
        if (!withPropertyValues || withPropertyValues.length < 1) return

        const property_id = withPropertyValues[0].property_id
        const property = properties[property_id]
        const assignedPropertyValues = convertPropertyValuesToAssignedPropertyValues(property, withPropertyValues)

        if (!assignedPropertyValues) return

        const response = await PropertyClient.updatePropertyAssignedProperty(
          property_id,
          assignee,
          assignedPropertyValues,
          payload,
          true,
        )
        if (response && response.type === SUCCESS_RESPONSE_TYPE) {
          if (response.data.assigned_property) {
            dispatch(upsertFilesProperties([response.data.assigned_property]))
          }
          runAfterTransitionEffects(assignee, payload)
        } else {
          showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
        }
      },
      [withPropertyValues, properties, dispatch],
    )

    const handleSuccess = async (response: ResponseData, payload: MaybeTransitionEffectPayload) => {
      if (!isMounted.current) return

      if (response.type === AssigneeType.Project) {
        return assignPropertyValues({ type: response.type, id: response.data.folder.id }, payload)
      }
      return assignPropertyValues({ type: response.type, id: response.data.article.id }, payload)
    }

    return (
      <AddFileInline
        viewType={viewType}
        emptyState={emptyState}
        onSuccess={handleSuccess}
        onCreateFile={onCreateFile}
        onUploadMediaFile={onUploadMediaFile}
      />
    )
  },
)
